import { Eye, EyeOff } from 'lucide-react'
import * as React from 'react'
import { cn } from '@/shared/lib/utils'
import { Input, type InputProps } from '@/shared/ui/input'

export type PasswordInputProps = Omit<InputProps, 'type'>

/**
 * Campo de contraseña con ojito para revelar lo escrito.
 *
 * ## Por qué existe
 *
 * Un campo de contraseña sin esto es una caja negra: escribís quince caracteres, ves quince
 * puntitos y no hay forma de saber si el gestor de contraseñas pisó el valor, si quedó el Bloq
 * Mayús puesto, o si al pegar se coló un espacio al final. En el diálogo de cambio de
 * contraseña es peor todavía: la validación de «coinciden» da verde igual cuando el gestor
 * llenó los DOS campos con lo mismo que vos no elegiste.
 *
 * ## Detalles que parecen de estilo y no lo son
 *
 * - **Alterna el `type` sobre el MISMO `<input>`.** Renderizar dos elementos distintos según el
 *   estado lo remonta: se pierde el foco y el cursor salta al final en medio de la escritura.
 * - **El botón es `type="button"`.** Sin eso, dentro de un `<form>` un click envía el
 *   formulario — o sea, «quiero mirar lo que escribí» dispararía el submit.
 * - **Arranca oculto siempre y no recuerda nada.** Revelar es un riesgo frente a quien mire la
 *   pantalla: que sea una decisión deliberada cada vez, no un estado que sobrevive.
 * - **No toca `autoComplete`.** Pasa las props tal cual; si se rompiera, los gestores dejan de
 *   ofrecer guardar la contraseña nueva y el usuario se queda sin su red de seguridad.
 * - **Queda en el orden de tabulación.** Es un control real: quien navega con teclado también
 *   necesita verificar lo que escribió.
 */
export const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, disabled, ...props }, ref) => {
    const [visible, setVisible] = React.useState(false)

    return (
      <div className="relative">
        <Input
          {...props}
          ref={ref}
          disabled={disabled}
          type={visible ? 'text' : 'password'}
          className={cn('pr-10', className)}
        />
        <button
          type="button"
          disabled={disabled}
          onClick={() => setVisible((current) => !current)}
          aria-label={visible ? 'Ocultar contraseña' : 'Mostrar contraseña'}
          aria-pressed={visible}
          className={cn(
            'absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-md',
            'text-muted-foreground transition-colors hover:text-foreground',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            'disabled:pointer-events-none disabled:opacity-50'
          )}
        >
          {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </button>
      </div>
    )
  }
)
PasswordInput.displayName = 'PasswordInput'
